import { createServiceModule, inject } from '@aesop-fables/containr';
import { Api, Authentication } from '@3nickels/data-modules';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Disposable } from '../../hooks/useConstant';

export interface RegisterUserData {
  birthdate?: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
}

export interface AuthWizardState {
  loginData: Authentication.LoginData;
  registerData: RegisterUserData;
  loginResultData: Api.LoginResultRest;
  authMethod?: Api.LoginTwoFactorData;
  accountSources?: Api.AccountSourceEnum[];
}

export const AuthWizardStateDefault: AuthWizardState = {
  loginData: {},
  registerData: {},
  loginResultData: {},
  authMethod: undefined,
};

export const CELL_COUNT = 6;

const mapRegisterUserDataToRegisterUserRequest = (
  data: RegisterUserData
): Api.RegisterUserRequest => {
  const { emailAddress, firstName, lastName, password, birthdate } = data;
  return { emailAddress, firstName, lastName, password, birthdate: birthdate };
};

export const signUpLoginWizardKey = 'services/signUpLoginWizard';

class SignUpLoginWizard implements Disposable {
  private readonly state: BehaviorSubject<AuthWizardState> = new BehaviorSubject(
    AuthWizardStateDefault
  );

  private userInfoAccepted = new Subject<boolean | undefined>();
  private readonly signUpLoginInProgress: BehaviorSubject<boolean | undefined> =
    new BehaviorSubject<boolean | undefined>(undefined);

  constructor(
    @inject(Api.ApiKeys.AuthApi)
    private readonly authApi: Api.AuthApi
  ) {
    this.signUpLoginInProgress.next(false);
  }

  get state$(): Observable<AuthWizardState> {
    return this.state;
  }

  get userInfoAccepted$(): Observable<boolean | undefined> {
    return this.userInfoAccepted;
  }

  get signUpLoginInProgressValue(): boolean | undefined {
    return this.signUpLoginInProgress.getValue();
  }

  setSignUpLoginInProgress(value: boolean) {
    this.signUpLoginInProgress.next(value);
  }

  async evaluateUsernameExists(data: Api.UsernameDto): Promise<Api.UsernameExistsDto> {
    const response = await this.authApi.evaluateUsernameExists(data);

    return response.data;
  }

  async usernameExistsEvaluator(data: Api.UsernameDto): Promise<Api.UsernameExistsDto> {
    const current = this.state.value;
    const { username } = data;

    const response = await this.evaluateUsernameExists(data);
    if (response.exists) {
      this.state.next({
        ...current,
        loginData: { username },
        accountSources: response.accountSources,
      });
    } else {
      await this.setUsernameAndBeginRegistration(username ?? '');
    }
    this.signUpLoginInProgress.next(true);
    return response;
  }

  async setUsernameAndBeginRegistration(username: string): Promise<void> {
    const current = this.state.value;
    const registerData: RegisterUserData = {
      emailAddress: username,
    };
    this.state.next({
      ...current,
      registerData,
      accountSources: undefined,
    });
  }

  async updateRegisterData(values: RegisterUserData): Promise<void> {
    const current = this.state.value;
    this.state.next({
      ...current,
      registerData: values,
    });
  }

  async finishRegistrationAndAttemptSignUp(
    values: RegisterUserData,
    captchaToken: string
  ): Promise<void> {
    await this.updateRegisterData(values);

    const payload: Api.RegisterUserRequest = mapRegisterUserDataToRegisterUserRequest(values);
    const result = await this.attemptSignUp(payload, captchaToken);
    if (result) {
      this.userInfoAccepted.next(result);
    }
  }

  async attemptSignUp(
    registerData: Api.RegisterUserRequest,
    captchaToken: string
  ): Promise<boolean> {
    const response = await this.authApi.registerUserV2(registerData, captchaToken);
    return response.status === 200;
  }

  async resendRegistrationCode(): Promise<void> {
    await this.authApi.resendRegistrationCode();
  }

  async saveLoginDataAndAttemptLogin(
    password: string,
    captchaToken: string
  ): Promise<{ twoFactorEnabled: boolean | undefined; redirectUrl: string | undefined }> {
    const current = this.state.value;
    const loginData = { ...current.loginData, password };
    let loginResultData = AuthWizardStateDefault.loginResultData;
    if (loginData?.username && password) {
      const result = await this.attemptLogin(loginData.username, password, captchaToken);
      loginResultData = result;
    }
    this.state.next({ ...current, loginData, loginResultData, authMethod: undefined });
    return {
      twoFactorEnabled: loginResultData.twoFactorEnabled,
      redirectUrl: loginResultData.redirectUrl,
    };
  }

  async attemptLogin(
    username: string,
    password: string,
    captchaToken: string
  ): Promise<Api.LoginResultRest> {
    const result = await this.authApi.loginV2({ username, password }, captchaToken);
    return result.data;
  }

  async resendCode(authMethod: Api.LoginTwoFactorData | undefined): Promise<void> {
    if (typeof authMethod === 'undefined') return;

    const auth: Api.LoginTwoFactorData = {
      id: authMethod.id,
      twoFactorType: authMethod.twoFactorType,
    };

    await this.authApi.resendTwoFactor(auth);
  }

  async changeAuthMethod(authMethod: Api.LoginTwoFactorData): Promise<void> {
    const current = this.state.value;
    await this.resendCode(authMethod);
    this.state.next({ ...current, authMethod });
  }

  reset() {
    this.state.next(AuthWizardStateDefault);
  }

  dispose(): void {
    console.log('disposing SignUpLoginWizard');
  }
}

export const withSignUpLoginWizard = createServiceModule(signUpLoginWizardKey, (services) => {
  services.use<SignUpLoginWizard>(signUpLoginWizardKey, SignUpLoginWizard);
});

export { SignUpLoginWizard };
